import { runtimeConfig } from '@/shared/config'

// Единый канон времени экосистемы (golibinfra/tzkit). На проводе три разные
// сущности, которые нельзя смешивать:
//
//   момент      — RFC3339 в UTC
//   день        — "YYYY-MM-DD", зоны у него нет вовсе
//   время суток — "HH:MM"
//
// А зон — две, и путать их нельзя тем более.
//
//   Зона момента — зона УСТРОЙСТВА смотрящего: в ней момент показывается и в
//   ней читается набранное им время. Спрашивать её больше негде — люди летают,
//   и любая сохранённая зона отстаёт от человека молча.
//
//   Зона организации (`APP_TZ`) — календарные сутки: «сегодня», «просрочено»,
//   границы периода, срок-как-день. Она обязана быть одна на всех, иначе у
//   доски и отчёта нет общих границ.

/** Зона организации (IANA), в которой UI считает календарные сутки. */
export const APP_TZ = runtimeConfig.APP_TZ

/**
 * Зона момента — зона устройства. Читается на каждый вызов, а не один раз на
 * импорт: вкладка переживает и перелёт, и смену зоны в системе. Движок без
 * IANA-зоны (редкость) откатывается на зону организации.
 */
export function momentZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || APP_TZ
}

// Локаль ru; без date-библиотеки (dependencies.md). Форматтер создаётся лениво и
// кэшируется парой «набор + зона»: зона момента приходит на вызов (у каждого
// смотрящего своя), а конструктор Intl дорогой.
const formatters = new Map<string, Intl.DateTimeFormat>()

function formatter(
  name: string,
  tz: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${name}|${tz}`
  const cached = formatters.get(key)
  if (cached) return cached
  const made = new Intl.DateTimeFormat(locale, { ...options, timeZone: tz })
  formatters.set(key, made)
  return made
}

// Абсолютный рендер: таймстампы `16.07.2026, 14:32`, дни — `16.07.2026`.
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
}

const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}

const SHORT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...SHORT_DATE_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
}

// Дни форматируются из своих же компонентов, а не через Date: строка
// "2026-08-01" — это календарный день без зоны, и прогон её через Date дал бы
// полночь UTC, которая западнее UTC рисуется предыдущим числом.
const MONTHS_RU = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const

// День с бэка приходит голым `YYYY-MM-DD`; таймстампы несут время (T…).
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

/** Календарный день `YYYY-MM-DD`: у него нет ни времени, ни зоны. */
export type Day = string

export function isDateOnly(iso: string | null | undefined): boolean {
  return typeof iso === 'string' && DATE_ONLY_RE.test(iso.trim())
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Разбор дня на компоненты; не день — null. */
function dayParts(value: string | null | undefined): [number, number, number] | null {
  if (!isDateOnly(value)) return null
  const [y, m, d] = (value as string).trim().split('-').map(Number)
  if (y == null || m == null || d == null) return null
  return [y, m, d]
}

// Компоненты момента в заданной зоне — единственный способ спросить «какое там
// число и который час», не полагаясь на зону процесса.
const PARTS_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

function zoneParts(d: Date, tz: string): { day: Day; hour: string; minute: string } {
  const parts = formatter('parts', tz, 'en-CA', PARTS_OPTIONS).formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00'
  // en-CA даёт ISO-порядок компонентов даты, поэтому день собирается прямо из них.
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    // 24-часовой формат может отдать «24» на полуночи — нормализуем.
    hour: get('hour') === '24' ? '00' : get('hour'),
    minute: get('minute'),
  }
}

/** День, на который приходится момент: календарный вопрос — зона организации. */
export function dayOf(iso: string | null | undefined, tz: string = APP_TZ): Day {
  const d = parse(iso)
  return d ? zoneParts(d, tz).day : ''
}

/** Сегодняшний день в зоне организации. */
export function todayInAppTz(now: Date = new Date(), tz: string = APP_TZ): Day {
  return zoneParts(now, tz).day
}

/** День значения: у дня — он сам, у момента — его день в зоне организации. */
export function dayValue(iso: string | null | undefined, tz: string = APP_TZ): Day {
  if (!iso) return ''
  if (isDateOnly(iso)) return iso.trim()
  return dayOf(iso, tz)
}

/** Дата словами. Число — календарный вопрос, поэтому зона организации. */
export function formatDate(iso: string, tz: string = APP_TZ): string {
  const parts = dayParts(iso)
  if (parts) {
    const [y, m, d] = parts
    const month = MONTHS_RU[m - 1]
    if (month) return `${d} ${month} ${y} г.`
    return ''
  }
  const dt = parse(iso)
  return dt ? formatter('date', tz, 'ru-RU', DATE_OPTIONS).format(dt) : ''
}

/** Момент словами с временем — в зоне того, кто смотрит. */
export function formatDateTime(iso: string, tz: string = momentZone()): string {
  const dt = parse(iso)
  return dt ? formatter('dateTime', tz, 'ru-RU', DATE_TIME_OPTIONS).format(dt) : ''
}

/** `08.07.2026` — компактно, для плотных таблиц/бейджей срока (день). */
export function formatShortDate(iso: string, tz: string = APP_TZ): string {
  const parts = dayParts(iso)
  if (parts) {
    const [y, m, d] = parts
    return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`
  }
  const dt = parse(iso)
  return dt ? formatter('shortDate', tz, 'ru-RU', SHORT_DATE_OPTIONS).format(dt) : ''
}

/** `08.07.2026, 14:32` — компактный таймстамп (24h) в зоне смотрящего. */
export function formatShortDateTime(iso: string, tz: string = momentZone()): string {
  const dt = parse(iso)
  return dt ? formatter('shortDateTime', tz, 'ru-RU', SHORT_DATE_TIME_OPTIONS).format(dt) : ''
}

// Абсолютный рендер значения: день → без времени и без зоны, момент → с
// временем в зоне смотрящего (дата у него та же зона, иначе число и часы
// разъедутся).
export function formatStamp(iso: string, tz: string = momentZone()): string {
  return isDateOnly(iso) ? formatShortDate(iso) : formatShortDateTime(iso, tz)
}

// День на проводе — уже `YYYY-MM-DD`, ровно то, что ждёт <input type="date">:
// прогонять его через Date незачем и нельзя (сдвинет на сутки).
export function toDateInput(day: Day | null | undefined): string {
  if (isDateOnly(day)) return (day as string).trim()
  // Момент днём быть не может, но старые кэши и мок-фикстуры ещё могут его
  // принести — берём его день в зоне организации.
  return dayValue(day)
}

/** Значение <input type="date"> → день контракта; пусто/мусор → null. */
export function dateInputToDay(value: string): Day | null {
  const v = value.trim()
  return isDateOnly(v) ? v : null
}

// Момент ↔ <input type="datetime-local"> — обе стороны в зоне устройства: поле
// показывает и принимает время того, кто на него смотрит. Прочитать набранное
// «15:00» зоной организации значило бы записать не тот момент — сотрудник в
// Челябинске получил бы встречу на два часа раньше задуманного.
export function toDateTimeInput(iso: string | null | undefined, tz: string = momentZone()): string {
  const d = parse(iso)
  if (!d) return ''
  const { day, hour, minute } = zoneParts(d, tz)
  return `${day}T${hour}:${minute}`
}

/** Значение <input type="datetime-local"> → RFC3339 в UTC; пусто/мусор → null. */
export function dateTimeInputToIso(value: string, tz: string = momentZone()): string | null {
  const v = value.trim()
  if (v === '') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v)
  if (!m) return null
  const [, y, mo, d, h, mi] = m
  const utcGuess = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi))
  // Смещение зоны берётся в самой точке: у зон с переводом стрелок оно зависит
  // от даты, поэтому константы тут быть не может.
  const offset = zoneOffsetMs(new Date(utcGuess), tz)
  const at = new Date(utcGuess - offset)
  // Второй проход ловит границу перевода стрелок: смещение вокруг неё меняется.
  const corrected = new Date(utcGuess - zoneOffsetMs(at, tz))
  return Number.isNaN(corrected.getTime()) ? null : corrected.toISOString()
}

// Смещение зоны от UTC в конкретный момент. У зон с переводом стрелок оно
// зависит от даты, поэтому константы тут быть не может.
function zoneOffsetMs(at: Date, tz: string): number {
  const { day, hour, minute } = zoneParts(at, tz)
  const parts = dayParts(day)
  if (!parts) return 0
  const [y, m, d] = parts
  const asUtc = Date.UTC(y, m - 1, d, Number(hour), Number(minute))
  // Секунды и миллисекунды в сетку зоны не входят — сравниваем по минутам.
  return asUtc - Math.floor(at.getTime() / 60_000) * 60_000
}

// Просрочка — вопрос про день, а не про момент, ровно как на бэке: задача и
// счёт со сроком «сегодня» просрочены не раньше конца сегодняшних суток зоны
// организации. У момента (срок шага сделки) бэк сравнивает сами моменты — здесь
// то же самое.
export function isOverdue(
  iso: string | null | undefined,
  now: Date = new Date(),
  tz: string = APP_TZ,
): boolean {
  if (!iso) return false
  if (isDateOnly(iso)) return iso.trim() < todayInAppTz(now, tz)
  const d = parse(iso)
  return d ? d.getTime() < now.getTime() : false
}

// Календарных дней до срока (отрицательное — просрочено на N дней), считая в
// зоне организации. Для подсветки «скоро».
export function daysUntil(
  iso: string | null | undefined,
  now: Date = new Date(),
  tz: string = APP_TZ,
): number | null {
  const target = dayValue(iso, tz)
  if (!target) return null
  return dayDiff(todayInAppTz(now, tz), target)
}

function dayDiff(from: Day, to: Day): number {
  const a = dayParts(from)
  const b = dayParts(to)
  if (!a || !b) return 0
  return Math.round(
    (Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / (24 * 3600_000),
  )
}

// Подпись зоны в UI: имя зоны + смещение («Москва · UTC+3»). Считается через
// Intl, а не хардкодится — зона переводит стрелки сама. По умолчанию подписывает
// зону момента; календарные сутки подписываются явным `APP_TZ`.
export function zoneLabel(now: Date = new Date(), tz: string = momentZone()): string {
  const short = zoneTag(now, tz)
  const offset = zoneOffsetLabel(now, tz)
  return short !== offset ? `${short} · ${offset}` : offset
}

/**
 * Короткий тег зоны — им подписываются моменты в интерфейсе («14:32 Москва»).
 * Имя человеческое не у каждой зоны: где его нет, Intl отдаёт GMT-фоллбек,
 * который к смещению ничего не добавляет — тогда показываем само смещение.
 * Литерала зоны в UI нет нигде: всё считается из самой зоны.
 */
export function zoneTag(now: Date = new Date(), tz: string = momentZone()): string {
  const name = zoneAbbr(now, tz)
  return name && !/^(GMT|UTC)/i.test(name) ? name : zoneOffsetLabel(now, tz)
}

/** Полная форма зоны момента для title — она же объясняет, чьё это время. */
export function zoneTitle(tz: string = momentZone()): string {
  return `Время показано в зоне вашего устройства (${tz})`
}

/** Полная форма зоны организации: ею считаются календарные сутки, общие для всех. */
export function orgZoneTitle(): string {
  return `Календарные сутки считаются в зоне организации (${APP_TZ})`
}

const ZONE_NAME_OPTIONS: Intl.DateTimeFormatOptions = { timeZoneName: 'longGeneric' }

/**
 * Короткое имя зоны в локали интерфейса.
 *
 * НЕ `shortGeneric`: он локализован только в части движков — Node отдаёт
 * «Москва», а Chromium на той же ru-локали «Moscow» латиницей посреди русского
 * интерфейса, и юнит-тесты под Node этого не ловят. `longGeneric` локализован
 * в обоих, а первая часть до запятой («Москва, стандартное время») и есть
 * короткое имя. `short` тоже мимо: на ru он вырождается в «GMT+3».
 */
export function zoneAbbr(now: Date = new Date(), tz: string = momentZone()): string {
  const parts = formatter('zoneName', tz, 'ru-RU', ZONE_NAME_OPTIONS).formatToParts(now)
  const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  return name.split(',')[0]?.trim() ?? ''
}

export function zoneOffsetLabel(now: Date = new Date(), tz: string = momentZone()): string {
  const minutes = Math.round(zoneOffsetMs(now, tz) / 60_000)
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`
}

/** Зона устройства как её отдал движок; пусто — движок её не знает. */
export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
}

// Расхождение зон — не ошибка, а повод сказать вслух, что календарные сутки
// («сегодня», «просрочено», период) считаются не по часам устройства.
export function isDeviceZoneMismatch(now: Date = new Date()): boolean {
  const device = deviceTimeZone()
  if (!device || device === APP_TZ) return false
  const deviceOffset = -new Date(now).getTimezoneOffset() * 60_000
  return deviceOffset !== zoneOffsetMs(now, APP_TZ)
}
